import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Settings = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [settings, setSettings] = useState({
    airtime: true,
    data: true,
    tv: true,
    electricity: true
  });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeProvider, setActiveProvider] = useState('easyaccess');
  const [upgradeCost, setUpgradeCost] = useState(1000);
  const [savingUpgrade, setSavingUpgrade] = useState(false);



  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchSettings = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/settings`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings({
            airtime: data.airtime !== false,
            data: data.data !== false,
            tv: data.tv !== false,
            electricity: data.electricity !== false,
        });
      }

      const pRes = await fetch(`${apiUrl}/api/v1/admin/provider-settings`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (pRes.ok) {
        const pData = await pRes.json();
        setActiveProvider(pData.active_data_provider || 'easyaccess');
      }

      const uRes = await fetch(`${apiUrl}/api/v1/admin/upgrade-settings`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (uRes.ok) {
        const uData = await uRes.json();
        setUpgradeCost(uData.upgrade_cost || 1000);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchSettings();
    }
  }, [user, fetchSettings]);

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/settings`, {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        alert("Settings updated successfully!");
      } else {
        alert("Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      alert("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleProviderChange = async (provider) => {
    setActiveProvider(provider);
    setSaving(true);
    try {
      const idToken = await user.getIdToken();
      await fetch(`${apiUrl}/api/v1/admin/provider-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ active_data_provider: provider })
      });
      localStorage.removeItem('vtu_data_plans_cache');
      localStorage.removeItem('vtu_data_plans_cache_time');
      alert('Provider settings updated! Cache cleared.');
    } catch (e) {
      console.error(e);
      alert('Error updating provider');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncDataPlans = async () => {
    try {
      setSyncing(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/scheduler/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        localStorage.removeItem('vtu_data_plans_cache');
        localStorage.removeItem('vtu_data_plans_cache_time');
        alert("Data plans sync started! They will be updated momentarily. Cache cleared.");
      } else {
        alert("Failed to trigger sync");
      }
    } catch (error) {
      console.error("Error triggering sync:", error);
      alert("Error triggering sync");
    } finally {
      setSyncing(false);
    }
  };



  const handleSaveUpgrade = async () => {
    try {
      setSavingUpgrade(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/upgrade-settings`, {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ upgrade_cost: parseFloat(upgradeCost) || 1000 })
      });
      if (res.ok) {
        alert("Upgrade settings saved successfully!");
      } else {
        alert("Failed to save upgrade settings");
      }
    } catch (error) {
      console.error("Error saving upgrade settings:", error);
      alert("Error saving upgrade settings");
    } finally {
      setSavingUpgrade(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-4xl mx-auto py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
              System Settings
              {loading && <span className="ml-4 text-sm font-normal text-gray-500 animate-pulse">Loading...</span>}
            </h2>
          </div>

          {!user ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                <p>Please log in to view this page.</p>
            </div>
          ) : (
            <div className="space-y-6">
              
              {/* Service Toggles */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    Service Toggles
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Turn specific VTU services on or off. Disabled services will reject purchase attempts from users.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {['airtime', 'data', 'tv', 'electricity'].map((service) => (
                        <div key={service} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <span className="capitalize font-medium text-gray-700 dark:text-gray-300">{service}</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input 
                                    type="checkbox" 
                                    className="sr-only peer"
                                    checked={settings[service]}
                                    onChange={() => handleToggle(service)}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    ))}
                </div>
                <div className="mt-6 flex justify-end">
                    <button 
                        onClick={handleSaveSettings}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
              </div>

              {/* Data Plans Scheduler & Provider Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    API & Data Plans Settings
                </h3>
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Active Data Provider</label>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Select which API provider to use for data plan purchases and syncing.</p>
                    <div className="flex space-x-6">
                        <label className="inline-flex items-center">
                            <input type="radio" className="form-radio text-blue-600 focus:ring-blue-500 w-5 h-5" name="provider" value="easyaccess" checked={activeProvider === 'easyaccess'} onChange={() => handleProviderChange('easyaccess')} disabled={saving} />
                            <span className="ml-2 text-gray-700 dark:text-gray-300 font-medium">EasyAccess</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input type="radio" className="form-radio text-blue-600 focus:ring-blue-500 w-5 h-5" name="provider" value="gladtidings" checked={activeProvider === 'gladtidings'} onChange={() => handleProviderChange('gladtidings')} disabled={saving} />
                            <span className="ml-2 text-gray-700 dark:text-gray-300 font-medium">GladTidings</span>
                        </label>
                    </div>
                </div>
                
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-md font-medium text-gray-800 dark:text-gray-200 mb-2">Data Plans Scheduler</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                        The background scheduler runs automatically at 1:00 AM daily to fetch updated prices from external API endpoints (EasyAccess/GladTidings) and writes them to Firestore.
                        You can also force an immediate update below.
                    </p>
                    <button 
                        onClick={handleSyncDataPlans}
                        disabled={syncing}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50 flex items-center"
                    >
                        <svg className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        {syncing ? 'Syncing...' : 'Sync Data Plans Now'}
                    </button>
                </div>
              </div>
              {/* Upgrade User Settings */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                    Account Upgrade Settings
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                    Specify the one-time fee users must pay to upgrade their account to 'reseller' status.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="w-full sm:w-1/2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Upgrade Cost (₦)</label>
                        <input 
                            type="number"
                            min="0"
                            className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            value={upgradeCost}
                            onChange={(e) => setUpgradeCost(e.target.value)}
                            disabled={savingUpgrade}
                        />
                    </div>
                    <div className="w-full sm:w-auto mt-4 sm:mt-6">
                        <button 
                            onClick={handleSaveUpgrade}
                            disabled={savingUpgrade}
                            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50"
                        >
                            {savingUpgrade ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
              </div>

            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Settings;
